import { Mistral } from "@mistralai/mistralai";
import { URL_DATA } from "customGlobal";
import auth from "auth";
import * as pdfjsLib from "pdfjs-dist";

export interface DocumentAnalysisResult {
  analysis: string;
  documentId: string;
  documentName: string;
  documentType: string;
  analysisDate: string;
}

/**
 * Extrait le texte d'un document selon son type
 */
async function extractTextFromDocument(fileBuffer: ArrayBuffer, fileType: string): Promise<string> {
  try {
    let extractedText = "";
    fileType = fileType.toLowerCase();

    if (fileType === "pdf") {
      try {
        // @ts-ignore
        const pdfjsWorker = await import("pdfjs-dist/build/pdf.worker.entry");
        // Définir le worker uniquement lors de l'extraction de PDF
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

        const loadingTask = pdfjsLib.getDocument({ data: fileBuffer });
        const pdf = await loadingTask.promise;

        let text = "";
        const maxPages = Math.min(pdf.numPages, 50); // Limite à 50 pages maximum

        for (let i = 1; i <= maxPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const strings = content.items
            .filter((item: any) => item.str)
            .map((item: any) => item.str);
          text += strings.join(" ") + "\n";
        }

        if (pdf.numPages > maxPages) {
          text += "\n[Document tronqué après " + maxPages + " pages...]";
        }

        extractedText = text;
      } catch (pdfError) {
        console.error("Erreur lors de l'extraction du PDF:", pdfError);
        extractedText = "[Erreur lors de l'extraction du contenu PDF]";
      }
    } else if (fileType === "docx" || fileType === "doc") {
      // Non supporté nativement par le navigateur
      extractedText =
        "[Format " +
        fileType.toUpperCase() +
        ": contenu non extractible directement dans le navigateur]";
    } else if (fileType === "xlsx" || fileType === "xls") {
      // Non supporté nativement par le navigateur
      extractedText =
        "[Format " +
        fileType.toUpperCase() +
        ": contenu non extractible directement dans le navigateur]";
    } else if (
      fileType === "csv" ||
      fileType === "txt" ||
      fileType === "text" ||
      fileType === "md" ||
      fileType === "json"
    ) {
      try {
        const decoder = new TextDecoder("utf-8");
        extractedText = decoder.decode(fileBuffer);
      } catch (e) {
        extractedText = "[Erreur lors de l'extraction du texte]";
      }
    } else if (fileType === "pptx") {
      extractedText = "[Format PowerPoint: contenu non extractible directement]";
    } else {
      extractedText = `[Format ${fileType} non pris en charge pour l'extraction de texte]`;
    }

    return extractedText || "[Extraction du contenu impossible]";
  } catch (error) {
    console.error("Erreur lors de l'extraction du contenu:", error);
    return `[Erreur lors de l'extraction: ${
      error instanceof Error ? error.message : "Erreur inconnue"
    }]`;
  }
}

export async function analyzeDocumentWithMistral(document: {
  id: string;
  nom: string;
  mimeType: string;
  date: string;
}): Promise<DocumentAnalysisResult> {
  try {
    // Création d'une instance Mistral avec la clé API depuis les variables d'environnement
    const MISTRAL_API_KEY = import.meta.env.VITE_MISTRAL_API_KEY;
    // const MISTRAL_API_KEY = "ySqXc5y65jdxRL9w79wdNXGwzCJi3doq";
    const mistralClient = new Mistral({
      apiKey: MISTRAL_API_KEY
    });
    if (!MISTRAL_API_KEY) {
      throw new Error(
        "Clé API Mistral non configurée, veuillez contacter AXIN si vous souhaitez cette fonctionnalité"
      );
    }
    const documentUrl = `${URL_DATA()}/document/${document.id}?&access_token=${auth.token}`;
    const response = await fetch(documentUrl);
    if (!response.ok) {
      throw new Error(
        `Impossible de récupérer le document (${response.status}: ${response.statusText})`
      );
    }

    const fileBuffer = await response.arrayBuffer();
    const fileType =
      document.nom
        .split(".")
        .pop()
        ?.toLowerCase() || "unknown";

    let documentContent = await extractTextFromDocument(fileBuffer, fileType);

    if (!documentContent || documentContent.trim() === "") {
      documentContent = "[Le document semble être vide ou dans un format non lisible]";
    }

    // Limiter la taille du contenu pour l'analyse
    let documentContentForAnalysis = documentContent;
    if (documentContent.length > 20000) {
      // Pour les très longs documents, extraire le début, le milieu et la fin
      const beginning = documentContent.substring(0, 8000);
      const middle = documentContent.substring(
        Math.floor(documentContent.length / 2) - 2000,
        Math.floor(documentContent.length / 2) + 2000
      );
      const end = documentContent.substring(documentContent.length - 8000);

      documentContentForAnalysis =
        beginning +
        "\n\n[...Contenu tronqué...]\n\n" +
        middle +
        "\n\n[...Contenu tronqué...]\n\n" +
        end;

      documentContentForAnalysis +=
        "\n\n[Le document complet est très volumineux, seules des parties ont été retenues pour l'analyse]";
    }

    const fileName = document.nom;
    const hasMinimalContent =
      documentContent.length > 50 &&
      !documentContent.includes("[Le document est vide]") &&
      !documentContent.includes("[Erreur lors de l'extraction");

    // Construction du prompt en fonction du contenu
    const analysisPrompt = hasMinimalContent
      ? `
        Analyse ce document "${fileName}" et fournis un résumé concis avec les informations clés.
        Format ton résumé de manière structurée avec:
        1. Un titre qui décrit le contenu général (en gras)
        2. Les points clés (uniquement ceux qui sont explicitement mentionnés dans le document)
        3. Un court résumé global (1-2 phrases)

        INSTRUCTIONS DE FORMATAGE:
        - Utilise un Markdown riche et bien formaté
        - Les titres doivent être précédés de ## (niveau 2)
        - Mets les termes importants en gras avec **terme**
        - Utilise des listes à puces avec - pour les points clés
        - Sépare clairement les sections avec des sauts de ligne
        - Structure ta réponse pour faciliter la lecture

        TRÈS IMPORTANT:
        - Ne JAMAIS inventer d'informations qui ne sont pas explicitement présentes dans le document
        - Si le document contient peu d'informations, ton analyse doit être courte et refléter fidèlement ce manque
        - Si tu n'as pas assez d'informations pour un point spécifique, indique-le clairement

        Contenu du document:
        ---
        ${documentContentForAnalysis}
        ---
        `
      : `
        Voici le contenu du document "${fileName}":
        ---
        ${documentContentForAnalysis}
        ---

        Ce document contient très peu de texte ou est potentiellement vide. 
        Indique simplement ce que tu peux voir dans le document, sans faire d'hypothèses 
        ni ajouter d'informations qui n'y figurent pas explicitement.
        Ton analyse doit être brève et fidèle au contenu limité.

        INSTRUCTIONS DE FORMATAGE:
        - Utilise un Markdown riche et bien formaté
        - Les titres doivent être précédés de ## (niveau 2)
        - Mets les termes importants en gras avec **terme**
        `;

    // Analyse du document avec Mistral
    const analysisResponse = await mistralClient.chat.complete({
      model: "mistral-medium",
      messages: [
        {
          role: "system",
          content:
            "Tu es un assistant spécialisé dans l'analyse fidèle de documents. Tu ne dois JAMAIS inventer des informations qui ne sont pas explicitement mentionnées dans le document. Si un document contient peu d'informations, ton analyse doit être brève et refléter ce manque."
        },
        {
          role: "user",
          content: analysisPrompt
        }
      ],
      maxTokens: 512,
      temperature: 0.1 // Température basse pour des réponses plus déterministes
    });

    const analysis = analysisResponse.choices?.[0]?.message?.content;

    if (!analysis) {
      throw new Error("Pas de réponse de l'assistant");
    }
    return {
      analysis: typeof analysis === "string" ? analysis : analysis.join(""),
      documentId: document.id,
      documentName: document.nom,
      documentType: fileType,
      analysisDate: new Date().toISOString()
    };
  } catch (error) {
    console.error("Erreur lors de l'analyse avec Mistral:", error);
    // En cas d'erreur, nous retournons l'erreur dans l'analyse
    return {
      analysis: `Une erreur est survenue lors de l'analyse: ${
        error instanceof Error ? error.message : "Erreur inconnue"
      }`,
      documentId: document.id,
      documentName: document.nom,
      documentType:
        document.nom
          .split(".")
          .pop()
          ?.toLowerCase() || "unknown",
      analysisDate: new Date().toISOString()
    };
  }
}
